import React, { useState, useEffect } from 'react';
import { Download, RefreshCw, Smartphone, ExternalLink } from 'lucide-react';
import { useRegisterSW } from 'virtual:pwa-register/react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export const PWAUpdater: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showComponent, setShowComponent] = useState(false);

  // PWA update handling
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      console.log('SW Registered for PWA Updater');
    },
    onRegisterError(error) {
      console.log('SW registration error in PWA Updater', error);
    },
  });

  useEffect(() => {
    // Check if app is installed
    const checkInstallStatus = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                          (window.navigator as any).standalone ||
                          document.referrer.includes('android-app://');
      setIsInstalled(isStandalone);
    };

    checkInstallStatus();

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Only show if on the PWA manager page or if there's an update
      if (window.location.pathname === '/pwa-manager' || needRefresh) {
        setShowComponent(true);
      }
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setShowComponent(false); // Hide after install
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Only show component if:
    // 1. There's an update available, OR
    // 2. We're on the PWA manager page, OR
    // 3. URL has install parameters (shared link)
    const urlParams = new URLSearchParams(window.location.search);
    const isInstallLink = urlParams.has('utm_campaign') && urlParams.get('utm_campaign') === 'pwa_install';
    const isOnPWAPage = window.location.pathname === '/pwa-manager';

    if (needRefresh || isOnPWAPage || isInstallLink) {
      setShowComponent(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [needRefresh]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        setIsInstalled(true);
      } else {
        console.log('User dismissed the install prompt');
      }

      setDeferredPrompt(null);
    } catch (error) {
      console.error('Error during install:', error);
    }
  };

  const handleUpdateClick = () => {
    updateServiceWorker(true);
  };

  const handleShareClick = async () => {
    const shareData = {
      title: 'Bradanna Field App',
      text: 'Install the Bradanna Field App for easy access to time tracking, jobs, and truck tickets',
      url: window.location.origin
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(`${shareData.title}\n${shareData.text}\n${shareData.url}`);
        alert('App link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const generateInstallLink = () => {
    const baseUrl = window.location.origin;
    const installParams = new URLSearchParams({
      utm_source: 'manual_install',
      utm_medium: 'link',
      utm_campaign: 'pwa_install'
    });
    return `${baseUrl}/?${installParams.toString()}`;
  };

  // Don't show if explicitly hidden or no reason to show
  if (!showComponent) {
    return null;
  }

  // Don't show on regular pages unless there's an update or install prompt
  const isOnPWAPage = window.location.pathname === '/pwa-manager';
  const urlParams = new URLSearchParams(window.location.search);
  const isInstallLink = urlParams.has('utm_campaign') && urlParams.get('utm_campaign') === 'pwa_install';

  if (!isOnPWAPage && !needRefresh && !deferredPrompt && !isInstallLink) {
    return null;
  }

  const styles = {
    container: {
      position: 'fixed' as const,
      bottom: '20px',
      right: '20px',
      zIndex: 1000,
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      padding: '16px',
      minWidth: '280px',
      maxWidth: '320px',
      border: '1px solid #e2e8f0'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '12px',
      color: '#1e293b',
      fontSize: '16px',
      fontWeight: '600'
    },
    buttonGroup: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '8px'
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 16px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      textDecoration: 'none',
      justifyContent: 'center',
      width: '100%'
    },
    updateButton: {
      backgroundColor: '#22c55e',
      color: 'white'
    },
    installButton: {
      backgroundColor: '#3b82f6',
      color: 'white'
    },
    shareButton: {
      backgroundColor: '#f8fafc',
      color: '#374151',
      border: '1px solid #e2e8f0'
    },
    linkButton: {
      backgroundColor: '#f59e0b',
      color: 'white'
    },
    closeButton: {
      position: 'absolute' as const,
      top: '8px',
      right: '8px',
      background: 'none',
      border: 'none',
      fontSize: '18px',
      cursor: 'pointer',
      color: '#9ca3af',
      width: '24px',
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    linkDisplay: {
      backgroundColor: '#f8fafc',
      padding: '8px',
      borderRadius: '6px',
      fontSize: '12px',
      fontFamily: 'monospace',
      color: '#374151',
      wordBreak: 'break-all' as const,
      marginTop: '8px',
      border: '1px solid #e2e8f0'
    }
  };

  return (
    <div style={styles.container}>
      <button
        style={styles.closeButton}
        onClick={() => setShowComponent(false)}
        title="Close"
      >
        ×
      </button>

      <div style={styles.header}>
        <Smartphone size={20} />
        PWA Manager
      </div>

      <div style={styles.buttonGroup}>
        {needRefresh && (
          <button
            style={{...styles.button, ...styles.updateButton}}
            onClick={handleUpdateClick}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#16a34a'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#22c55e'}
          >
            <RefreshCw size={16} />
            Update App
          </button>
        )}

        {deferredPrompt && !isInstalled && (
          <button
            style={{...styles.button, ...styles.installButton}}
            onClick={handleInstallClick}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
          >
            <Download size={16} />
            Install App
          </button>
        )}

        <button
          style={{...styles.button, ...styles.shareButton}}
          onClick={handleShareClick}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
        >
          <ExternalLink size={16} />
          Share App
        </button>

        <button
          style={{...styles.button, ...styles.linkButton}}
          onClick={() => {
            const link = generateInstallLink();
            navigator.clipboard.writeText(link);
            alert('Install link copied to clipboard!');
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#d97706'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f59e0b'}
        >
          <ExternalLink size={16} />
          Copy Install Link
        </button>
      </div>

      <div style={styles.linkDisplay}>
        {generateInstallLink()}
      </div>

      <div style={{fontSize: '12px', color: '#64748b', marginTop: '8px', lineHeight: '1.4'}}>
        {isInstalled ? '✅ App is installed' : '📱 App can be installed'}
        {needRefresh && ' • 🚀 Update available'}
      </div>
    </div>
  );
};